/**
 * RichContentBlocksEditor — admin UI for editing the rich blocks consumed by
 * `RichContentBlocks`. Inline, no modal.
 *
 * Each block is `{ id, type, data }`. State is owned by the parent; this
 * component is fully controlled via `value` / `onChange`.
 *
 * Supported types:
 *   text   — rich textarea (uses RichTextEditor if available, fallback textarea)
 *   button — label / url / style / size / align
 *   image  — uploaded image + alt + optional link + align + width + caption
 */
import React, { useState } from 'react';
import {
  Plus, Trash2, GripVertical, Type, Image as ImageIcon, MousePointerClick,
  ChevronUp, ChevronDown, Link as LinkIcon, Video, AlertCircle, CheckCircle2,
  Images, X as XIcon, FileText, Paperclip,
} from 'lucide-react';
import { api, absoluteUrl } from '../lib/api';
import { BUTTON_STYLE_OPTIONS, BUTTON_SIZE_OPTIONS, parseVideoUrl, formatBytes } from './RichContentBlocks';
import MediaLibraryPicker from './MediaLibraryPicker';

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

const BLOCK_TEMPLATES = {
  text: () => ({ id: newId(), type: 'text', data: { html: '' } }),
  button: () => ({ id: newId(), type: 'button', data: { label: 'Saber más', url: '', style: 'primary', size: 'md', align: 'left' } }),
  image: () => ({ id: newId(), type: 'image', data: { url: '', alt: '', link: '', align: 'center', width: 'lg', caption: '' } }),
  video: () => ({ id: newId(), type: 'video', data: { url: '', caption: '', align: 'center', width: 'lg' } }),
  gallery: () => ({ id: newId(), type: 'gallery', data: { items: [], columns: 3, display: 'grid', caption: '' } }),
  document: () => ({ id: newId(), type: 'document', data: { url: '', title: '', description: '', size_bytes: 0, extension: '', original_filename: '', filename: '' } }),
};

export default function RichContentBlocksEditor({ value, onChange }) {
  const blocks = Array.isArray(value) ? value : [];

  function updateBlock(id, patch) {
    onChange(blocks.map((b) => (b.id === id ? { ...b, data: { ...b.data, ...patch } } : b)));
  }
  function addBlock(type) {
    onChange([...blocks, BLOCK_TEMPLATES[type]()]);
  }
  function removeBlock(id) {
    onChange(blocks.filter((b) => b.id !== id));
  }
  function moveBlock(id, dir) {
    const idx = blocks.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    const out = blocks.slice();
    [out[idx], out[target]] = [out[target], out[idx]];
    onChange(out);
  }

  return (
    <div className="border border-gray-200 rounded-xl bg-gray-50/40" data-testid="rcb-editor">
      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-y-3 bg-white rounded-t-xl">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-charcoal-800">Contenido enriquecido</p>
          <p className="text-xs text-charcoal-500">Agrega texto, botones e imágenes con hipervínculo</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button type="button" onClick={() => addBlock('text')} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md hover:bg-gray-50" data-testid="rcb-add-text">
            <Type size={12} /> Texto
          </button>
          <button type="button" onClick={() => addBlock('button')} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md hover:bg-gray-50" data-testid="rcb-add-button">
            <MousePointerClick size={12} /> Botón
          </button>
          <button type="button" onClick={() => addBlock('image')} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md hover:bg-gray-50" data-testid="rcb-add-image">
            <ImageIcon size={12} /> Imagen
          </button>
          <button type="button" onClick={() => addBlock('video')} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md hover:bg-gray-50" data-testid="rcb-add-video">
            <Video size={12} /> Video
          </button>
          <button type="button" onClick={() => addBlock('gallery')} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md hover:bg-gray-50" data-testid="rcb-add-gallery">
            <Images size={12} /> Galería
          </button>
          <button type="button" onClick={() => addBlock('document')} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md hover:bg-gray-50" data-testid="rcb-add-document">
            <Paperclip size={12} /> Documento
          </button>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {blocks.length === 0 && (
          <p className="text-xs text-charcoal-400 italic text-center py-6">
            Sin bloques. Usa los botones de arriba para añadir contenido.
          </p>
        )}
        {blocks.map((b, idx) => (
          <BlockEditor
            key={b.id}
            block={b}
            isFirst={idx === 0}
            isLast={idx === blocks.length - 1}
            onUpdate={(patch) => updateBlock(b.id, patch)}
            onRemove={() => removeBlock(b.id)}
            onMove={(dir) => moveBlock(b.id, dir)}
          />
        ))}
      </div>
    </div>
  );
}

function BlockEditor({ block, isFirst, isLast, onUpdate, onRemove, onMove }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid={`rcb-block-${block.type}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-charcoal-500">
          <GripVertical size={12} className="text-charcoal-300" />
          {block.type === 'text' ? 'Texto' : block.type === 'button' ? 'Botón' : block.type === 'video' ? 'Video' : block.type === 'gallery' ? 'Galería' : block.type === 'document' ? 'Documento' : 'Imagen'}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={isFirst} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" data-testid="rcb-move-up">
            <ChevronUp size={14} />
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={isLast} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" data-testid="rcb-move-down">
            <ChevronDown size={14} />
          </button>
          <button type="button" onClick={onRemove} className="p-1 rounded text-red-500 hover:bg-red-50" data-testid="rcb-remove">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {block.type === 'text' && (
        <textarea
          value={block.data.html || ''}
          onChange={(e) => onUpdate({ html: e.target.value })}
          placeholder="<p>Escribe contenido HTML aquí. Tags soportados: p, strong, em, ul, ol, li, a, h2, h3, blockquote.</p>"
          rows={5}
          className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 font-mono text-xs"
          data-testid="rcb-text-field"
        />
      )}

      {block.type === 'button' && <ButtonFields data={block.data} onUpdate={onUpdate} />}
      {block.type === 'image' && <ImageFields data={block.data} onUpdate={onUpdate} />}
      {block.type === 'video' && <VideoFields data={block.data} onUpdate={onUpdate} />}
      {block.type === 'gallery' && <GalleryFields data={block.data} onUpdate={onUpdate} />}
      {block.type === 'document' && <DocumentFields data={block.data} onUpdate={onUpdate} />}
    </div>
  );
}

function ButtonFields({ data, onUpdate }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Texto del botón">
        <input value={data.label || ''} onChange={(e) => onUpdate({ label: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" data-testid="rcb-btn-label" />
      </Field>
      <Field label="URL del enlace">
        <div className="flex items-center gap-1">
          <LinkIcon size={12} className="text-charcoal-400 ml-1" />
          <input value={data.url || ''} onChange={(e) => onUpdate({ url: e.target.value })} placeholder="https://… o /es/contacto" className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" data-testid="rcb-btn-url" />
        </div>
      </Field>
      <Field label="Estilo">
        <select value={data.style || 'primary'} onChange={(e) => onUpdate({ style: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" data-testid="rcb-btn-style">
          {BUTTON_STYLE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Tamaño">
        <select value={data.size || 'md'} onChange={(e) => onUpdate({ size: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" data-testid="rcb-btn-size">
          {BUTTON_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Alineación">
        <select value={data.align || 'left'} onChange={(e) => onUpdate({ align: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5">
          <option value="left">Izquierda</option>
          <option value="center">Centrado</option>
          <option value="right">Derecha</option>
        </select>
      </Field>
    </div>
  );
}

function ImageFields({ data, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: res } = await api.post('/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUpdate({ url: res.url || '' });
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('No se pudo subir la imagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <div className="space-y-3">
      <Field label="Imagen">
        {data.url ? (
          <div className="flex items-start gap-3">
            <img src={absoluteUrl(data.url)} alt="" className="w-24 h-24 object-cover rounded-md border border-gray-200" />
            <div className="flex-1 flex flex-col gap-1">
              <button type="button" onClick={() => onUpdate({ url: '' })} className="text-xs text-red-600 underline">Quitar imagen</button>
              <p className="text-[11px] text-charcoal-400 truncate">{data.url}</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-cmic-50 hover:bg-cmic-100 text-cmic-700 rounded-md cursor-pointer text-xs font-medium">
              {uploading ? 'Subiendo…' : 'Subir imagen'}
              <input type="file" accept="image/*,.heic,.heif" hidden onChange={handleFile} disabled={uploading} data-testid="rcb-img-upload" />
            </label>
            <button type="button" onClick={() => setShowPicker(true)} data-testid="rcb-img-pick"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-charcoal-600 rounded-md text-xs font-medium">
              <ImageIcon size={12} /> Elegir de archivos
            </button>
          </div>
        )}
      </Field>
      {showPicker && (
        <MediaLibraryPicker
          filterType="image"
          title="Elegir imagen de la biblioteca"
          onSelect={({ url }) => { onUpdate({ url }); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Texto alternativo (alt)">
          <input value={data.alt || ''} onChange={(e) => onUpdate({ alt: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" />
        </Field>
        <Field label="Enlace al hacer clic (opcional)">
          <input value={data.link || ''} onChange={(e) => onUpdate({ link: e.target.value })} placeholder="https://…" className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" data-testid="rcb-img-link" />
        </Field>
        <Field label="Alineación">
          <select value={data.align || 'center'} onChange={(e) => onUpdate({ align: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5">
            <option value="left">Izquierda</option>
            <option value="center">Centrado</option>
            <option value="right">Derecha</option>
          </select>
        </Field>
        <Field label="Tamaño">
          <select value={data.width || 'lg'} onChange={(e) => onUpdate({ width: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5">
            <option value="sm">Pequeño</option>
            <option value="md">Mediano</option>
            <option value="lg">Grande</option>
            <option value="full">Ancho completo</option>
          </select>
        </Field>
      </div>
      <Field label="Pie de foto (opcional)">
        <input value={data.caption || ''} onChange={(e) => onUpdate({ caption: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" />
      </Field>
    </div>
  );
}

function VideoFields({ data, onUpdate }) {
  const parsed = parseVideoUrl(data.url || '');
  const [uploading, setUploading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [error, setError] = useState('');
  const isFile = parsed?.provider === 'file';

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      setError('El video supera 100 MB.');
      e.target.value = '';
      return;
    }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const { data: res } = await api.post('/admin/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUpdate({ url: res.url || '' });
    } catch (err) {
      setError(formatVideoErr(err) || 'No se pudo subir el video.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <div className="space-y-3">
      <Field label="Video">
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-cmic-50 hover:bg-cmic-100 text-cmic-700 rounded-md cursor-pointer text-xs font-medium">
            <Video size={12} /> {uploading ? 'Subiendo…' : 'Subir video'}
            <input type="file" accept="video/*,.mp4,.webm,.mov,.m4v" hidden onChange={handleFile} disabled={uploading} data-testid="rcb-video-upload" />
          </label>
          <button type="button" onClick={() => setShowPicker(true)} data-testid="rcb-video-pick"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-charcoal-600 rounded-md text-xs font-medium">
            <Video size={12} /> Elegir de archivos
          </button>
        </div>
        <p className="text-[11px] text-charcoal-400 mt-1">Sube un archivo (MP4/WebM/MOV, hasta 100 MB) o pega una URL de YouTube/Vimeo abajo.</p>
      </Field>

      {error && <div className="text-xs bg-rose-50 text-rose-700 border border-rose-100 rounded-md px-2 py-1.5 flex items-center gap-1.5"><AlertCircle size={12} />{error}</div>}

      {isFile && (
        <div className="rounded-md border border-gray-200 overflow-hidden">
          <video src={absoluteUrl(parsed.src)} controls preload="metadata" className="w-full max-h-56 bg-black" data-testid="rcb-video-preview" />
          <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50">
            <span className="text-[11px] text-charcoal-500 truncate">{data.url}</span>
            <button type="button" onClick={() => onUpdate({ url: '' })} className="text-xs text-red-600 underline flex-shrink-0 ml-2">Quitar video</button>
          </div>
        </div>
      )}

      <Field label="URL del video (YouTube o Vimeo)">
        <div className="flex items-center gap-1">
          <LinkIcon size={12} className="text-charcoal-400 ml-1" />
          <input
            value={isFile ? '' : (data.url || '')}
            onChange={(e) => onUpdate({ url: e.target.value })}
            placeholder="https://youtube.com/watch?v=… ó https://vimeo.com/…"
            disabled={isFile}
            className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 disabled:bg-gray-50 disabled:text-charcoal-400"
            data-testid="rcb-video-url"
          />
        </div>
      </Field>
      {data.url && !isFile && (
        <div
          className={`text-xs flex items-center gap-1.5 px-2 py-1.5 rounded-md ${parsed ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}
        >
          {parsed ? (
            <>
              <CheckCircle2 size={12} /> URL válida ({parsed.provider}, id: {parsed.id})
            </>
          ) : (
            <>
              <AlertCircle size={12} /> URL no reconocida. Solo se soportan YouTube y Vimeo (o sube un archivo).
            </>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Alineación">
          <select value={data.align || 'center'} onChange={(e) => onUpdate({ align: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5">
            <option value="left">Izquierda</option>
            <option value="center">Centrado</option>
            <option value="right">Derecha</option>
          </select>
        </Field>
        <Field label="Tamaño">
          <select value={data.width || 'lg'} onChange={(e) => onUpdate({ width: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5">
            <option value="sm">Pequeño</option>
            <option value="md">Mediano</option>
            <option value="lg">Grande</option>
            <option value="full">Ancho completo</option>
          </select>
        </Field>
      </div>
      <Field label="Pie del video (opcional)">
        <input value={data.caption || ''} onChange={(e) => onUpdate({ caption: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" />
      </Field>

      {showPicker && (
        <MediaLibraryPicker
          filterType="video"
          title="Elegir video de la biblioteca"
          onSelect={({ url }) => { onUpdate({ url }); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

function formatVideoErr(err) {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : '';
}

function GalleryFields({ data, onUpdate }) {
  const items = Array.isArray(data.items) ? data.items : [];
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  function setItems(next) { onUpdate({ items: next }); }

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setError('');
    setProgress({ done: 0, total: files.length });

    const CONCURRENCY = 4; // upload at most 4 at a time so the network isn't saturated
    async function uploadOne(f) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const { data: res } = await api.post('/upload-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.url ? { ok: true, item: { id: Math.random().toString(36).slice(2, 10), url: res.url, alt: '', caption: '' } } : { ok: false, name: f.name, msg: 'respuesta vacía' };
      } catch (err) {
        return { ok: false, name: f.name, msg: err?.response?.data?.detail || 'error de red' };
      } finally {
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }
    }

    try {
      const uploads = [];
      let accumulated = items.slice();
      for (let i = 0; i < files.length; i += CONCURRENCY) {
        const batch = files.slice(i, i + CONCURRENCY);
        // eslint-disable-next-line no-await-in-loop
        const settled = await Promise.all(batch.map(uploadOne));
        uploads.push(...settled);
        // Append successful uploads from each batch progressively so the admin
        // sees thumbnails appear as the upload advances.
        const ok = settled.filter((u) => u.ok).map((u) => u.item);
        if (ok.length) {
          accumulated = [...accumulated, ...ok];
          setItems(accumulated);
        }
      }
      const failed = uploads.filter((u) => !u.ok);
      if (failed.length) {
        const detail = failed.slice(0, 3).map((u) => `${u.name}: ${u.msg}`).join(' · ');
        setError(`No se pudieron subir ${failed.length} de ${files.length} imágenes. ${detail}`);
      }
    } finally {
      setUploading(false);
      setProgress({ done: 0, total: 0 });
      e.target.value = '';
    }
  }

  function removeItem(idx) { setItems(items.filter((_, i) => i !== idx)); }
  function moveItem(idx, dir) {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const out = items.slice();
    [out[idx], out[j]] = [out[j], out[idx]];
    setItems(out);
  }
  function updateItem(idx, patch) {
    setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Field label={`Imágenes (${items.length})`}>
          <span className="text-[11px] text-charcoal-400">
            Sube varias fotos a la vez (JPG, PNG, WebP, GIF o HEIC de iPhone). Se mostrarán en grid con lightbox.
          </span>
        </Field>
        <label className={`inline-flex items-center gap-2 px-3 py-1.5 bg-cmic-50 hover:bg-cmic-100 text-cmic-700 rounded-md text-xs font-medium flex-shrink-0 ${uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
          {uploading ? `Subiendo ${progress.done}/${progress.total}…` : (<><Plus size={12} /> Agregar fotos</>)}
          <input type="file" accept="image/*,.heic,.heif" multiple hidden onChange={handleFiles} disabled={uploading} data-testid="rcb-gallery-upload" />
        </label>
      </div>

      {uploading && (
        <div data-testid="rcb-gallery-progress">
          <div className="flex items-center justify-between text-[11px] text-charcoal-500 mb-1">
            <span>Subiendo imágenes…</span>
            <span>{progress.done}/{progress.total} ({pct}%)</span>
          </div>
          <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-cmic-500 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2 flex items-start gap-1.5" data-testid="rcb-gallery-error">
          <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> <span>{error}</span>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {items.map((it, idx) => (
            <div key={it.id || idx} className="relative group rounded-md overflow-hidden border border-gray-200 bg-gray-50">
              <img src={absoluteUrl(it.url)} alt={it.alt || ''} className="w-full aspect-square object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end opacity-0 group-hover:opacity-100">
                <div className="w-full p-1.5 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="flex items-center gap-0.5">
                    <button type="button" onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="p-1 rounded bg-white/20 text-white disabled:opacity-30">
                      <ChevronUp size={10} />
                    </button>
                    <button type="button" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="p-1 rounded bg-white/20 text-white disabled:opacity-30">
                      <ChevronDown size={10} />
                    </button>
                  </div>
                  <button type="button" onClick={() => removeItem(idx)} className="p-1 rounded bg-red-500/80 hover:bg-red-500 text-white">
                    <XIcon size={10} />
                  </button>
                </div>
              </div>
              <input
                value={it.alt || ''}
                onChange={(e) => updateItem(idx, { alt: e.target.value })}
                placeholder="alt / caption"
                className="w-full text-[10px] border-t border-gray-200 px-1.5 py-1 bg-white"
              />
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Visualización">
          <select value={data.display || 'grid'} onChange={(e) => onUpdate({ display: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" data-testid="rcb-gallery-display">
            <option value="grid">Cuadrícula</option>
            <option value="carousel">Carrusel</option>
          </select>
        </Field>
        <Field label="Columnas (solo cuadrícula)">
          <select value={data.columns || 3} onChange={(e) => onUpdate({ columns: parseInt(e.target.value, 10) })} disabled={data.display === 'carousel'} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 disabled:opacity-50 disabled:bg-gray-50">
            <option value={2}>2 columnas</option>
            <option value={3}>3 columnas</option>
            <option value={4}>4 columnas</option>
          </select>
        </Field>
      </div>
      {data.display === 'carousel' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-cmic-50/50 border border-cmic-100 rounded-md p-3">
          <Field label="Cambio automático">
            <label className="flex items-center gap-2 text-sm text-charcoal-700 cursor-pointer">
              <input type="checkbox" checked={!!data.autoplay} onChange={(e) => onUpdate({ autoplay: e.target.checked })} className="rounded border-gray-300 text-cmic-600 focus:ring-cmic-500" data-testid="rcb-gallery-autoplay" />
              Avanzar fotos solas
            </label>
          </Field>
          <Field label="Intervalo (segundos)">
            <input
              type="number" min={1} max={30} step={1}
              value={data.autoplay_seconds || 4}
              onChange={(e) => onUpdate({ autoplay_seconds: Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 4)) })}
              disabled={!data.autoplay}
              className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 disabled:opacity-50 disabled:bg-gray-50"
              data-testid="rcb-gallery-autoplay-seconds"
            />
          </Field>
        </div>
      )}
      <Field label="Pie de galería (opcional)">
        <input value={data.caption || ''} onChange={(e) => onUpdate({ caption: e.target.value })} className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" />
      </Field>
    </div>
  );
}

function DocumentFields({ data, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: res } = await api.post('/upload-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUpdate({
        url: res.url || '',
        filename: res.filename || '',
        original_filename: res.original_filename || file.name,
        size_bytes: res.size_bytes || 0,
        extension: res.extension || (file.name.split('.').pop() || '').toLowerCase(),
        title: data.title || res.original_filename || file.name,
      });
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo subir el archivo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <div className="space-y-3">
      <Field label="Archivo">
        {data.url ? (
          <div className="flex items-center gap-3 p-2 border border-gray-200 rounded-md bg-white">
            <FileText size={22} className="text-cmic-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-charcoal-800 truncate">{data.original_filename || data.filename}</p>
              <p className="text-[11px] text-charcoal-400">{(data.extension || '').toUpperCase()}{data.size_bytes ? ` · ${formatBytes(data.size_bytes)}` : ''}</p>
            </div>
            <button type="button" onClick={() => onUpdate({ url: '', filename: '', original_filename: '', size_bytes: 0, extension: '' })} className="text-xs text-red-600 underline flex-shrink-0">Quitar</button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-cmic-50 hover:bg-cmic-100 text-cmic-700 rounded-md cursor-pointer text-xs font-medium">
            {uploading ? 'Subiendo…' : (<><Plus size={12} /> Subir documento</>)}
            <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.txt,.csv,.zip" hidden onChange={handleFile} disabled={uploading} data-testid="rcb-doc-upload" />
          </label>
        )}
      </Field>
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2 flex items-center gap-1.5">
          <AlertCircle size={12} /> {error}
        </div>
      )}
      <Field label="Título visible (opcional)">
        <input value={data.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} placeholder="Ej: Programa del evento 2026" className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" data-testid="rcb-doc-title" />
      </Field>
      <Field label="Descripción (opcional)">
        <textarea value={data.description || ''} onChange={(e) => onUpdate({ description: e.target.value })} rows={2} placeholder="1-2 líneas que aparecerán bajo el título." className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 resize-y" />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-charcoal-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
